// src/components/settings/TeamManagementTab.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import InviteUserModal from '../InviteUserModal';

import {
  listUsers,
  updateUserRole as updateUserRoleApi,
  deleteUser as deleteUserApi,
  listInvites as listInvitesApi,
  resendInvite as resendInviteApi,
  cancelInvite as cancelInviteApi,
} from '../../services/userService';

import { usePlan } from '../../hooks/useBillingQueries';
import { isSubscriptionReadOnly } from '../../services/billingService';

const ROLES = ['admin', 'staff', 'viewer'];

export default function TeamManagementTab() {
  const [meId, setMeId] = useState(null);

  // Members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  // Invites
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // Billing summary via React Query (plan, seats, status)
  const {
    data: billingSummary,
    isLoading: loadingBilling,
  } = usePlan();

  // UI
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [includeInvited, setIncludeInvited] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');

  const filteredMembers = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (u) =>
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q))
    );
  }, [members, search]);

  const seatsInUse = billingSummary?.seatsInUse ?? null;
  const seatsPurchased = billingSummary?.seatsPurchased ?? null;
  const plan = billingSummary?.plan || null;

  // Derive subscription status + read-only flag
  const billingStatusRaw =
    billingSummary?.billingStatus ||
    billingSummary?.stripe?.subscriptionStatus ||
    null;

  const billingStatus = billingStatusRaw
    ? String(billingStatusRaw).toLowerCase()
    : null;

  const readOnly =
    billingStatus &&
    typeof isSubscriptionReadOnly === 'function'
      ? isSubscriptionReadOnly(billingStatus)
      : false;

  // Current user id
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/auth/profile');
        const id = String(res?.data?.id || res?.data?._id || '');
        if (id) setMeId(id);
      } catch {
        // non-blocking
      }
    })();
  }, []);

  // Load members + invites
  useEffect(() => {
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInvited, includeInactive]);

  async function reloadAll() {
    await Promise.all([loadMembers(), loadInvites()]);
  }

  async function loadMembers() {
    setLoadingMembers(true);
    try {
      const rows = await listUsers({
        search: '',
        includeInvited,
        includeInactive,
        page: 1,
        limit: 100, // small teams
      });
      setMembers(rows);
    } catch (e) {
      toast.error('Failed to load team members.');
    } finally {
      setLoadingMembers(false);
    }
  }

  async function loadInvites() {
    setLoadingInvites(true);
    try {
      const rows = await listInvitesApi();
      setInvites(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // If the endpoint doesn't exist yet, just hide the section silently.
      setInvites([]);
    } finally {
      setLoadingInvites(false);
    }
  }

  async function onChangeRole(user, role) {
    if (!user?.id) return;
    if (String(user.id) === String(meId)) {
      toast.error('You cannot change your own role.');
      return;
    }
    try {
      await updateUserRoleApi({ id: user.id, role });
      toast.success('Role updated.');
      await loadMembers();
    } catch (err) {
      if (err?._status === 409) {
        toast.error(
          err?._data?.message || 'Cannot change role of the last admin.'
        );
      } else {
        toast.error('Failed to update role.');
      }
    }
  }

  async function onRemoveUser(user) {
    if (!user?.id) return;
    if (String(user.id) === String(meId)) {
      toast.error('You cannot remove yourself.');
      return;
    }
    if (!window.confirm(`Remove ${user.name || user.email}?`)) return;

    try {
      await deleteUserApi(user.id);
      toast.success('User removed.');
      await loadMembers();
    } catch (err) {
      if (err?._status === 409) {
        toast.error(
          err?._data?.message || 'Cannot delete the last admin.'
        );
      } else {
        toast.error('Failed to remove user.');
      }
    }
  }

  async function onResendInvite(inv) {
    if (!inv?.id) return;
    setResendingId(inv.id);
    try {
      await resendInviteApi(inv.id);
      toast.success(`Invitation re-sent to ${inv.email}.`);
      await loadInvites();
    } catch (err) {
      if (err?._is429) {
        const after = err?._retryAfter
          ? ` Try again in ~${err._retryAfter}s.`
          : '';
        toast.warning(`Rate limit reached.${after}`);
      } else {
        toast.error('Failed to resend invite.');
      }
    } finally {
      setResendingId(null);
    }
  }

  async function onCancelInvite(inv) {
    if (!inv?.id) return;
    if (!window.confirm(`Cancel invite for ${inv.email}?`)) return;

    setCancellingId(inv.id);
    try {
      await cancelInviteApi(inv.id);
      toast.success('Invite cancelled.');
      await loadInvites();
      // Refresh members as well; invited ghost accounts may be present
      await loadMembers();
    } catch {
      toast.error('Failed to cancel invite.');
    } finally {
      setCancellingId(null);
    }
  }

  const handleOpenInviteModal = () => {
    if (readOnly) {
      toast.error(
        'Your subscription is inactive. Re-activate billing to add more team members.'
      );
      return;
    }
    setInviteModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header + primary action */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Team & seats
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage your workspace members. Owners, admins, and staff use paid seats; viewers are free.
          </p>
          {readOnly && (
            <p className="mt-1 text-xs text-rose-700">
              Your subscription is inactive. Re-activate billing to invite new team members.
            </p>
          )}
        </div>
        <button
          className="inline-flex items-center rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={handleOpenInviteModal}
          disabled={readOnly}
        >
          Invite user
        </button>
      </div>

      {/* Seat summary card (from billing) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {loadingBilling ? (
          <p className="text-xs text-slate-500">Loading seat usage…</p>
        ) : billingSummary ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Plan
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900 capitalize">
                {plan || 'Starter'}
              </p>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Seats in use
                </p>
                <p className="mt-1 text-xl font-semibold text-slate-900">
                  {seatsInUse ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Seats purchased
                </p>
                <p className="mt-1 text-xl font-semibold text-slate-900">
                  {seatsPurchased ?? '—'}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 max-w-xs">
              Adding an <span className="font-medium">admin</span> or{' '}
              <span className="font-medium">staff</span> may increase your Stripe seat count
              automatically. Use the Billing tab to manage your subscription.
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            Billing summary unavailable. Seats are still enforced on the backend.
          </p>
        )}
      </div>

      {/* Filters / search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search team…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black/10"
          />
          <label className="inline-flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-black focus:ring-black/20"
              checked={includeInvited}
              onChange={(e) => setIncludeInvited(e.target.checked)}
            />
            Show invited
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-black focus:ring-black/20"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
        </div>
      </div>

      {/* Members */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Team members
          </h3>
        </div>

        {loadingMembers ? (
          <div className="p-4 text-sm text-slate-600">
            Loading members…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Name
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Email
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Role
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Status
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Last login
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMembers.map((u) => (
                  <tr key={u.id}>
                    <td className="p-2 text-slate-900">
                      {u.name || '—'}
                    </td>
                    <td className="p-2 text-slate-700">{u.email}</td>
                    <td className="p-2">
                      <select
                        value={u.role}
                        onChange={(e) => onChangeRole(u, e.target.value)}
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black/10"
                        disabled={String(u.id) === String(meId)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <StatusChips invited={u.invited} active={u.active} />
                    </td>
                    <td className="p-2 text-slate-600">
                      {u.lastLoginAt
                        ? formatDateTime(u.lastLoginAt)
                        : '—'}
                    </td>
                    <td className="p-2">
                      {String(u.id) !== String(meId) && (
                        <button
                          className="text-xs font-medium text-red-600 hover:underline"
                          onClick={() => onRemoveUser(u)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredMembers.length === 0 && (
                  <tr>
                    <td
                      className="p-4 text-sm text-slate-500"
                      colSpan={6}
                    >
                      No team members found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Invites */}
      {!loadingInvites && invites.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Pending invites
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Email
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Role
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Invited
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Expires
                  </th>
                  <th className="p-2 text-left text-xs font-semibold uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td className="p-2 text-slate-900">
                      {inv.email}
                    </td>
                    <td className="p-2 text-slate-700 capitalize">
                      {inv.role || 'staff'}
                    </td>
                    <td className="p-2 text-slate-600">
                      {formatDateTime(
                        inv.invitedAt || inv.createdAt
                      )}
                    </td>
                    <td className="p-2 text-slate-600">
                      {formatDateTime(inv.expiresAt)}
                    </td>
                    <td className="p-2 space-x-3">
                      <button
                        className="text-xs font-medium text-blue-600 hover:underline disabled:opacity-50"
                        disabled={resendingId === inv.id}
                        onClick={() => onResendInvite(inv)}
                      >
                        {resendingId === inv.id
                          ? 'Resending…'
                          : 'Resend'}
                      </button>
                      <button
                        className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                        disabled={cancellingId === inv.id}
                        onClick={() => onCancelInvite(inv)}
                      >
                        {cancellingId === inv.id
                          ? 'Cancelling…'
                          : 'Cancel'}
                      </button>
                    </td>
                  </tr>
                ))}
                {invites.length === 0 && (
                  <tr>
                    <td
                      className="p-4 text-sm text-slate-500"
                      colSpan={5}
                    >
                      No pending invites.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviteModalOpen && (
        <InviteUserModal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          onSuccess={() => {
            loadMembers();
            loadInvites();
          }}
        />
      )}
    </div>
  );
}

function StatusChips({ invited, active }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${
          active
            ? 'bg-green-50 text-green-700 border-green-200'
            : 'bg-slate-50 text-slate-700 border-slate-200'
        }`}
      >
        {active ? 'Active' : 'Inactive'}
      </span>
      {invited && (
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
          Invited
        </span>
      )}
    </div>
  );
}

function formatDateTime(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}
